import { Fragment, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import {
  commitFolder,
  createFolderSession,
  deleteFolderSession,
  getFolderBatchJob,
  planFolderImport,
  uploadFolderArchive,
  uploadFolderFile,
  type FolderCommitResponse,
  type FolderFilePlan,
  type FolderPlanResponse,
  type FolderSheetPlan,
  type FolderSheetRef,
  type ImportSpec,
  type LoadMode,
} from '@/lib/api';
import {
  buildFolderRows,
  countFindings,
  describeRow,
  describeSheet,
  groupFindingsByFile,
  refLabel,
  sheetLoadKey,
  type FileFindings,
  type FolderFileRow,
} from '@/lib/folderFindings';
import { restatedCount, valueUpdates } from '@/lib/folderReport';
import { valueUpdatesSentence } from '@/lib/valueUpdates';
import { cn } from '@/lib/utils';
import { graphDisplayLabel, useGraphs } from '@/lib/useGraphs';
import {
  FolderAnalysisReport,
  type FolderReportActions,
  type MapContext,
  type FolderReportSection,
} from '@/components/FolderAnalysisReport';
import { messageClass } from '@/components/ui/Message';
import type { BarePrefixClaim } from '@/lib/api';
import { buttonVariants } from '@/components/ui/Button';
import { FolderMappingPanel } from '@/components/import/FolderMappingPanel';
import { linkFromSaved } from '@/lib/folderMapping';
import { describeApiError } from '@/lib/apiErrors';

type Phase = 'pick' | 'uploading' | 'analyzing' | 'plan' | 'error';

// A folder analysis survives navigating away to another route and back (UAT
// 2026-09-02: leaving for Browse to check for data lost the plan). Only the
// server-derived plan state is kept — the picked File objects cannot be
// serialized, so the "Map to the Spreadsheet wizard" hand-off needs a re-pick
// after a navigation; the load/commit flow works from the server-staged files.
// The staging session lives 30 min server-side; a restored plan whose session
// has expired meets a clear refusal on its next action, and the user re-analyzes.
const FOLDER_RESTORE_KEY = 'tres.folderImport.v1';

interface PersistedFolder {
  phase: Phase;
  plan: FolderPlanResponse;
  sessionId: string;
  included: Record<string, boolean>;
  graphChoice: Record<string, string>;
  modeChoice: Record<string, 'append' | 'replace'>;
  /** Files whose claim on the prefix `:` the person chose to convert (4.19). */
  convertChoice?: Record<string, boolean>;
  skippedLocal: string[];
  subfolderCount: number;
  /** A running/finished batch job — restored so returning to the tab shows
   *  live progress or the final report, never a silent "analyze" reset. */
  batchJobId?: string | null;
}

function loadPersistedFolder(): PersistedFolder | null {
  try {
    const raw = sessionStorage.getItem(FOLDER_RESTORE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedFolder;
    // Only a completed analysis is worth restoring; transient phases are not.
    return p.phase === 'plan' && p.plan && p.sessionId ? p : null;
  } catch {
    return null;
  }
}

/** Keyed by load key: a file's staged name, or `sheetLoadKey` of a sheet. */
type LoadRowState =
  | { state: 'loading' }
  | { state: 'loaded'; graph: string | null }
  | { state: 'failed'; message: string };

type FileEntry = {
  relative: string;
  graph: string;
  mode: LoadMode;
  action?: 'index';
  convert_bare_prefix?: boolean;
};
type SheetEntry = { relative: string; sheet: string; spec: ImportSpec };
interface BatchSelection {
  files: FileEntry[];
  sheets: SheetEntry[];
}

/** A Replace load waiting on the destructive-op gate — Replace drops the whole
 *  target graph, not just this file's previous contents. */
type ReplaceConfirm = { scope: 'all' } | { scope: 'item'; key: string } | null;

type Tone = 'plain' | 'muted' | 'success' | 'error';
interface RowStatus {
  label: string;
  tone: Tone;
}
const TONE_CLASS: Record<Tone, string> = {
  plain: '',
  muted: 'text-muted-foreground',
  success: 'text-tres-success',
  error: 'text-tres-error',
};

const CATALOG_GRAPH = 'urn:tres:catalog';

const MANAGED_GRAPH_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'urn:tres:ontology', label: 'Ontology' },
  { value: 'urn:tres:vocabulary', label: 'Vocabulary' },
  { value: 'urn:tres:data', label: 'Data' },
  { value: CATALOG_GRAPH, label: 'Catalog' },
];

const SELECT_CLASS = 'rounded border border-border bg-background px-1 py-0.5 text-xs';
const BUTTON_CLASS =
  'rounded border border-border px-2 py-0.5 hover:border-tres-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tres-action disabled:pointer-events-none disabled:opacity-40';

/**
 * Folder-scope import: pick a folder — its top-level contents only, no
 * zipping, no subfolders — stage every file server-side, and work the corpus
 * plan from one table of files. Everything the analysis found lives in the
 * analysis report, with the action that resolves each problem.
 */
export function FolderImportPanel({
  onMapSheet,
  uploadsLocked = false,
}: {
  /** Hand a staged sheet to the Spreadsheet wizard (the plan's Map action). */
  onMapSheet?: (file: File, sheet?: string, context?: MapContext) => void;
  /** A load holds the gate — analyzing (which stages files) is banned too. */
  uploadsLocked?: boolean;
} = {}) {
  const auth = useAuth();
  const idToken = auth.user?.id_token ?? '';
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  // Original File objects by staged relative path, so the Map action can hand
  // the actual bytes to the Spreadsheet wizard without a re-pick.
  const pickedRef = useRef<Map<string, File>>(new Map());
  // Read once: a plan persisted before a route change, restored so returning
  // lands on the plan rather than a blank picker.
  const restored = useRef(loadPersistedFolder()).current;
  const [phase, setPhase] = useState<Phase>(restored?.phase ?? 'pick');
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [skippedLocal, setSkippedLocal] = useState<string[]>(restored?.skippedLocal ?? []);
  const [subfolderCount, setSubfolderCount] = useState(restored?.subfolderCount ?? 0);
  const [plan, setPlan] = useState<FolderPlanResponse | null>(restored?.plan ?? null);
  const [sessionId, setSessionId] = useState<string | null>(restored?.sessionId ?? null);
  // User-editable dispositions: include key → include? Seeded from the server
  // plan's skip proposals; the checkbox and the report's choices override.
  const [included, setIncluded] = useState<Record<string, boolean>>(restored?.included ?? {});
  const [graphChoice, setGraphChoice] = useState<Record<string, string>>(
    restored?.graphChoice ?? {},
  );
  const [modeChoice, setModeChoice] = useState<Record<string, 'append' | 'replace'>>(
    restored?.modeChoice ?? {},
  );
  const [convertChoice, setConvertChoice] = useState<Record<string, boolean>>(
    restored?.convertChoice ?? {},
  );
  const [loadState, setLoadState] = useState<Record<string, LoadRowState>>({});
  const [batchRunning, setBatchRunning] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState<ReplaceConfirm>(null);
  const [batchReport, setBatchReport] = useState<FolderCommitResponse | null>(null);
  // The server-side batch job (202 + poll): progress survives navigation, and
  // the final report stays readable for a day — success and failure are both
  // always VISIBLE, never lost with the tab's memory (UAT 2026-09-03).
  const [batchJobId, setBatchJobId] = useState<string | null>(restored?.batchJobId ?? null);
  const [batchProgress, setBatchProgress] = useState<{
    done: number;
    total: number;
    current?: string;
  } | null>(null);
  const [report, setReport] = useState<{ focus: string | null } | null>(null);
  // The several-sheets pages over the plan's import sheets (4.11).
  const [mappingSheets, setMappingSheets] = useState(false);
  // The pages stay mounted once opened, hidden while the plan shows, so
  // stepping back to the plan and in again loses no answer.
  const [mappingOpened, setMappingOpened] = useState(false);
  // Managed graphs first, then any custom graphs on the instance — a folder
  // load can target every graph a direct upload can.
  const graphsQ = useGraphs();
  const GRAPH_OPTIONS = [
    ...MANAGED_GRAPH_OPTIONS,
    ...(graphsQ.data ?? [])
      .filter((g) => !g.managed)
      .map((g) => ({ value: g.bare, label: graphDisplayLabel(g) })),
  ];

  // Persist the plan across a route change; drop it when there is no plan to
  // keep (reset, or a new pick that has not analyzed yet).
  useEffect(() => {
    try {
      if (phase === 'plan' && plan && sessionId) {
        const snap: PersistedFolder = {
          phase,
          plan,
          sessionId,
          included,
          graphChoice,
          modeChoice,
          convertChoice,
          skippedLocal,
          subfolderCount,
          batchJobId,
        };
        sessionStorage.setItem(FOLDER_RESTORE_KEY, JSON.stringify(snap));
      } else if (phase === 'pick') {
        sessionStorage.removeItem(FOLDER_RESTORE_KEY);
      }
    } catch {
      /* sessionStorage unavailable — persistence is a convenience, not required */
    }
  }, [phase, plan, sessionId, included, graphChoice, modeChoice, convertChoice, skippedLocal, subfolderCount, batchJobId]);

  // The folder is a MIXED corpus: every importable format is admitted and the
  // server routes each file to its pathway (sheets map, graph/native files
  // load, documents are indexed in the catalog).
  const SUPPORTED_EXT =
    /\.(csv|tsv|xlsx|xls|ods|json|ttl|nt|tqd|tqs|pdf|txt|md|html|htm|doc|docx)$/i;

  async function analyze(files: File[]) {
    setError(null);
    setMappingSheets(false);
    setMappingOpened(false);
    setPlan(null);
    setIncluded({});
    setGraphChoice({});
    setLoadState({});
    pickedRef.current = new Map();
    // Folder CONTENTS only: after dropping the picked folder's own name, any
    // remaining path separator means the file lives in a subfolder — out of
    // scope.
    const scoped: Array<{ file: File; staged: string }> = [];
    let inSubfolders = 0;
    for (const f of files) {
      if (f.name.startsWith('.')) continue;
      const rel =
        (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      const parts = rel.split('/');
      const staged = parts.length > 1 ? parts.slice(1).join('/') : rel;
      if (staged.includes('/')) {
        inSubfolders++;
        continue;
      }
      scoped.push({ file: f, staged });
    }
    setSubfolderCount(inSubfolders);
    const admitted = scoped.filter((s) => SUPPORTED_EXT.test(s.staged));
    const ignored = scoped
      .filter((s) => !SUPPORTED_EXT.test(s.staged))
      .map((s) => s.staged);
    setSkippedLocal(ignored);
    if (admitted.length === 0) {
      setError(
        'The folder holds no importable files (spreadsheets, graph files, native QROS, or documents).',
      );
      setPhase('error');
      return;
    }
    setPhase('uploading');
    setProgress({ done: 0, total: admitted.length });
    const created = await createFolderSession(idToken);
    if (!created.ok) {
      setError(`${created.message}`);
      setPhase('error');
      return;
    }
    const sid = created.data.session_id;
    setSessionId(sid);
    for (const [i, s] of admitted.entries()) {
      const up = await uploadFolderFile(sid, s.staged, s.file, idToken);
      if (!up.ok) {
        setError(`${s.staged}: ${up.message}`);
        setPhase('error');
        void deleteFolderSession(sid, idToken);
        return;
      }
      pickedRef.current.set(s.staged, s.file);
      setProgress({ done: i + 1, total: admitted.length });
    }
    await planStaged(sid);
  }

  // A zip of a dataset: the server opens it and stages every file under its
  // folder, so ontology/, vocabulary/, catalog/ and data/ route themselves.
  async function analyzeArchive(file: File) {
    setError(null);
    setReport(null);
    setSkippedLocal([]);
    setSubfolderCount(0);
    setMappingSheets(false);
    setMappingOpened(false);
    setPlan(null);
    setIncluded({});
    setGraphChoice({});
    setLoadState({});
    pickedRef.current = new Map();
    setPhase('uploading');
    setProgress({ done: 0, total: 1 });
    const created = await createFolderSession(idToken);
    if (!created.ok) {
      setError(`${created.message}`);
      setPhase('error');
      return;
    }
    const sid = created.data.session_id;
    setSessionId(sid);
    const up = await uploadFolderArchive(sid, file, idToken);
    if (!up.ok) {
      setError(`${file.name}: ${up.message}`);
      setPhase('error');
      void deleteFolderSession(sid, idToken);
      return;
    }
    setSkippedLocal(up.data.skipped);
    setProgress({ done: 1, total: 1 });
    await planStaged(sid);
  }

  async function planStaged(sid: string) {
    setPhase('analyzing');
    const res = await planFolderImport(sid, idToken);
    if (!res.ok) {
      setError(`${res.message}`);
      setPhase('error');
      return;
    }
    const seeded: Record<string, boolean> = {};
    for (const sh of res.data.sheets) {
      // A sheet identical to one already loaded is left out, and its row
      // says so; ticking it loads it again.
      seeded[refLabel(sh.sheet)] = sh.disposition !== 'skip' && sh.known?.state !== 'unchanged';
    }
    for (const f of res.data.files) {
      seeded[f.relative] = f.disposition !== 'skip' && f.disposition !== 'ignore';
    }
    setIncluded(seeded);
    setPlan(res.data);
    setPhase('plan');
  }

  function reset() {
    if (sessionId) void deleteFolderSession(sessionId, idToken);
    setMappingSheets(false);
    setMappingOpened(false);
    setSessionId(null);
    setPlan(null);
    setError(null);
    setSkippedLocal([]);
    setSubfolderCount(0);
    setIncluded({});
    setGraphChoice({});
    setLoadState({});
    setReplaceConfirm(null);
    setReport(null);
    pickedRef.current = new Map();
    setPhase('pick');
    if (inputRef.current) inputRef.current.value = '';
    if (zipRef.current) zipRef.current.value = '';
  }

  function analyzeAgain() {
    reset();
    inputRef.current?.click();
  }

  const isOn = (key: string) => included[key] ?? true;

  function toggle(key: string) {
    setIncluded((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  }

  function setIncludedKey(key: string, on: boolean) {
    setIncluded((prev) => ({ ...prev, [key]: on }));
  }

  const graphLabel = (g: string) => GRAPH_OPTIONS.find((o) => o.value === g)?.label ?? g;

  // A file's target is the user's pick, else the plan's proposal; a file with
  // neither has no target and is not loaded until one is picked (74.3).
  function targetGraphOf(f: FolderFilePlan): string | null {
    return graphChoice[f.relative] ?? f.proposed_graph ?? null;
  }

  const loadable = (f: FolderFilePlan) => f.kind === 'graph' || f.kind === 'native';
  // A prose document: Load records what it is and what it mentions, in the
  // catalog, with no model (Tier 1). Actionable like a graph file.
  const indexable = (f: FolderFilePlan) => f.kind === 'document';
  const actionable = (f: FolderFilePlan) => loadable(f) || indexable(f);

  function fileEntry(f: FolderFilePlan): FileEntry | null {
    if (indexable(f)) {
      return { relative: f.relative, graph: CATALOG_GRAPH, mode: 'append', action: 'index' };
    }
    const graph = targetGraphOf(f);
    if (!graph) return null;
    return {
      relative: f.relative,
      graph,
      mode: modeChoice[f.relative] ?? 'append',
      ...(f.bare_prefix && convertChoice[f.relative] ? { convert_bare_prefix: true } : {}),
    };
  }

  /** Same resolution by staged name — for rows reported back by a batch poll. */
  function targetGraphOfName(name: string): string | null {
    const f = plan?.files.find((p) => p.relative === name);
    return f ? (fileEntry(f)?.graph ?? null) : (graphChoice[name] ?? null);
  }

  // A rough load-time estimate for the plan (UAT 2026-09-02). Ontology-graph
  // loads are far slower per byte than data/catalog — they materialize rules —
  // so ontology bytes are weighted heavily. Measured anchor: a 431 MB ontology
  // load took ~9.4 min (~0.75 MB/s); other graphs are far faster. Deliberately
  // conservative, and framed as an estimate.
  function estimateLoadSeconds(sel: BatchSelection): number {
    const ONTOLOGY_BPS = 0.75e6;
    const OTHER_BPS = 6e6;
    let secs = 0;
    for (const entry of sel.files) {
      const bytes = plan?.files.find((f) => f.relative === entry.relative)?.bytes ?? 0;
      const isOntology =
        entry.graph === 'urn:tres:ontology' || entry.graph.endsWith(':ontology');
      secs += bytes / (isOntology ? ONTOLOGY_BPS : OTHER_BPS);
    }
    return secs;
  }

  function formatLoadEstimate(secs: number): string {
    if (secs < 20) return 'a few seconds';
    if (secs < 90) return 'under a minute';
    const mins = Math.round(secs / 60);
    return `about ${mins} minute${mins === 1 ? '' : 's'}`;
  }

  function canMap(ref: FolderSheetRef): boolean {
    return !!onMapSheet && pickedRef.current.has(ref.file);
  }

  function mapSheet(ref: FolderSheetRef, context?: MapContext) {
    const file = pickedRef.current.get(ref.file);
    if (!file || !onMapSheet) return;
    setReport(null);
    onMapSheet(file, ref.sheet === 'Sheet1' ? undefined : ref.sheet, context);
  }

  // The batch fast path: everything still included and not yet loaded, in one
  // motion. Files carry their per-row graph/mode choices; import-disposition
  // sheets execute their proposed specs server-side.
  function batchSelection(): BatchSelection {
    if (!plan) return { files: [], sheets: [] };
    return {
      // A file with no target graph is left out of the batch until one is picked.
      files: plan.files
        .filter(
          (f) =>
            actionable(f) && isOn(f.relative) && loadState[f.relative]?.state !== 'loaded',
        )
        .map(fileEntry)
        .filter((entry): entry is FileEntry => entry !== null),
      sheets: plan.sheets.flatMap((s) =>
        s.spec &&
        s.disposition === 'import' &&
        isOn(refLabel(s.sheet)) &&
        loadState[sheetLoadKey(s.sheet)]?.state !== 'loaded'
          ? [{ relative: s.sheet.file, sheet: s.sheet.sheet, spec: s.spec }]
          : [],
      ),
    };
  }

  async function runBatch(sel: BatchSelection) {
    if (!sessionId || batchRunning) return;
    const keys = [
      ...sel.files.map((f) => f.relative),
      ...sel.sheets.map((s) => sheetLoadKey({ file: s.relative, sheet: s.sheet })),
    ];
    if (keys.length === 0) return;
    setReplaceConfirm(null);
    setBatchRunning(true);
    setBatchReport(null);
    setLoadState((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = { state: 'loading' };
      return next;
    });
    const res = await commitFolder(sessionId, sel.files, sel.sheets, idToken);
    if (!res.ok) {
      setBatchRunning(false);
      setBatchReport({
        items: [],
        loaded: 0,
        failed: keys.length,
        graphs: [],
        note: res.message,
      });
      setLoadState((prev) => {
        const next = { ...prev };
        for (const k of keys) next[k] = { state: 'failed', message: res.message };
        return next;
      });
      return;
    }
    // The batch is a server-side job now; the poll below carries progress and
    // the final report — including after navigating away and back.
    setBatchJobId(res.data.job_id);
  }

  /** Load one file or sheet again through the batch path. */
  function retry(key: string) {
    if (!plan) return;
    const f = plan.files.find((p) => p.relative === key);
    if (f) {
      const entry = fileEntry(f);
      if (!entry) {
        setError(`${f.relative} has no target graph; pick one in its row.`);
        return;
      }
      const confirmed = replaceConfirm?.scope === 'item' && replaceConfirm.key === key;
      if (entry.mode === 'replace' && !confirmed) {
        setReplaceConfirm({ scope: 'item', key });
        return;
      }
      setIncludedKey(f.relative, true);
      void runBatch({ files: [entry], sheets: [] });
      return;
    }
    const s = plan.sheets.find((p) => sheetLoadKey(p.sheet) === key);
    if (!s?.spec) return;
    setIncludedKey(refLabel(s.sheet), true);
    void runBatch({
      files: [],
      sheets: [{ relative: s.sheet.file, sheet: s.sheet.sheet, spec: s.spec }],
    });
  }

  /** Fold a poll's item list into the per-row states. */
  function applyBatchItems(items: FolderCommitResponse['items'], current?: string) {
    setLoadState((prev) => {
      const next = { ...prev };
      for (const it of items) {
        next[it.item] = it.ok
          ? {
              state: 'loaded',
              graph: it.item.includes(' :: ') ? null : targetGraphOfName(it.item),
            }
          : { state: 'failed', message: it.detail };
      }
      if (current) next[current] = { state: 'loading' };
      return next;
    });
  }

  // Poll the batch job while it runs; one fetch on (re)mount renders a
  // finished job's durable report. Only a 404 (the job aged out past its day,
  // or a fresh server) stops tracking — its loads are in the transaction log.
  // A 401 from an id-token that expired mid-batch, or a transient 5xx, leaves
  // the running batch untouched and retries: the result surfaces the moment the
  // session is valid again. A running batch is never given up for lost.
  useEffect(() => {
    if (!batchJobId || !idToken) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      const res = await getFolderBatchJob(batchJobId, idToken);
      if (stopped) return;
      if (!res.ok) {
        if (res.status === 404) {
          setBatchJobId(null);
          setBatchRunning(false);
        } else {
          timer = setTimeout(() => void tick(), 5000);
        }
        return;
      }
      const s = res.data;
      applyBatchItems(s.progress.items, s.progress.current);
      setBatchProgress({
        done: s.progress.done,
        total: s.progress.total,
        ...(s.progress.current ? { current: s.progress.current } : {}),
      });
      if (s.state === 'running') {
        setBatchRunning(true);
        timer = setTimeout(() => void tick(), 2000);
        return;
      }
      setBatchRunning(false);
      setBatchProgress(null);
      if (s.state === 'done' && s.result) {
        setBatchReport(s.result);
        if (s.result.loaded > 0) invalidateAfterDataWrite(queryClient);
      } else if (s.state === 'failed') {
        setBatchReport({
          items: s.progress.items,
          loaded: s.progress.items.filter((i) => i.ok).length,
          failed: s.progress.items.filter((i) => !i.ok).length,
          graphs: [],
          note: `The batch did not finish. ${describeApiError(s.http_status ?? 500, s.error, s.detail)}`,
        });
      }
    };
    void tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJobId, idToken]);

  // The design law: decisions scale with PATTERNS — one control per proposed
  // destination sets the whole group. But the proposal is a content-sniff
  // GUESS, so every file row is also individually correctable (UAT 2026-09-03).
  // A diverged group reads "mixed", never the first file's choice.
  function setGroup(files: FolderFilePlan[], patch: { graph?: string; mode?: 'append' | 'replace' }) {
    const { graph, mode } = patch;
    if (graph !== undefined) {
      setGraphChoice((prev) => {
        const next = { ...prev };
        for (const f of files) next[f.relative] = graph;
        return next;
      });
    }
    if (mode !== undefined) {
      setModeChoice((prev) => {
        const next = { ...prev };
        for (const f of files) next[f.relative] = mode;
        return next;
      });
    }
  }

  const rows = plan ? buildFolderRows(plan) : [];
  const failures: Record<string, string> = {};
  for (const [key, row] of Object.entries(loadState)) {
    if (row.state === 'failed') failures[key] = row.message;
  }
  const findings = plan ? groupFindingsByFile(plan, failures) : new Map<string, FileFindings>();
  const totals = countFindings(findings.values());
  const reportSections: FolderReportSection[] = rows.flatMap((r) => {
    const f = findings.get(r.relative);
    return f ? [{ relative: r.relative, summary: describeRow(r), findings: f }] : [];
  });
  const reportActions: FolderReportActions = {
    isIncluded: isOn,
    setIncluded: setIncludedKey,
    canMap,
    map: mapSheet,
    retry,
    cancelRetry: () => setReplaceConfirm(null),
    confirmingRetry:
      replaceConfirm?.scope === 'item'
        ? {
            key: replaceConfirm.key,
            graphLabel: graphLabel(targetGraphOfName(replaceConfirm.key) ?? 'no graph picked'),
          }
        : null,
    loadRunning: batchRunning,
    analyzeAgain,
    analyzeAgainDisabled: uploadsLocked || batchRunning,
  };

  function statusText(s: RowStatus) {
    return <span className={TONE_CLASS[s.tone]}>{s.label}</span>;
  }

  function fileStatus(f: FolderFilePlan): RowStatus {
    const row = loadState[f.relative];
    if (row?.state === 'loaded') {
      return { label: row.graph ? `Loaded into ${graphLabel(row.graph)}` : 'Loaded', tone: 'success' };
    }
    if (row?.state === 'loading') return { label: 'Loading…', tone: 'muted' };
    if (row?.state === 'failed') return { label: 'Failed', tone: 'error' };
    if (!actionable(f)) return { label: f.kind === 'other' ? 'Left out' : 'Skipped', tone: 'muted' };
    if (!isOn(f.relative)) return { label: 'Skipped', tone: 'muted' };
    return { label: indexable(f) ? 'Ready to index' : 'Ready to load', tone: 'plain' };
  }

  function sheetStatus(s: FolderSheetPlan): RowStatus {
    const row = loadState[sheetLoadKey(s.sheet)];
    if (row?.state === 'loaded') return { label: 'Loaded', tone: 'success' };
    if (row?.state === 'loading') return { label: 'Loading…', tone: 'muted' };
    if (row?.state === 'failed') return { label: 'Failed', tone: 'error' };
    const loaded = s.known?.loads[0];
    const on = loaded ? new Date(loaded.imported_at).toLocaleDateString() : '';
    if (!isOn(refLabel(s.sheet))) {
      return s.known?.state === 'unchanged'
        ? { label: `Already loaded on ${on}; left out`, tone: 'muted' }
        : { label: 'Skipped', tone: 'muted' };
    }
    if (s.disposition !== 'import' || !s.spec) return { label: 'Map to load', tone: 'plain' };
    if (s.known?.state === 'unchanged') return { label: `Already loaded on ${on}; will load again`, tone: 'plain' };
    if (s.known?.state === 'new_version') return { label: `New version of the sheet loaded on ${on}`, tone: 'plain' };
    return { label: 'Ready to load', tone: 'plain' };
  }

  function workbookStatus(sheets: FolderSheetPlan[]): RowStatus {
    const states = sheets.map((s) => loadState[sheetLoadKey(s.sheet)]?.state);
    const n = (st: LoadRowState['state']) => states.filter((x) => x === st).length;
    if (n('loading') > 0) return { label: 'Loading…', tone: 'muted' };
    if (n('failed') > 0) return { label: `${n('failed')} of ${sheets.length} failed`, tone: 'error' };
    if (n('loaded') > 0) return { label: `${n('loaded')} of ${sheets.length} loaded`, tone: 'success' };
    return { label: '', tone: 'plain' };
  }

  function findingsButton(relative: string) {
    const g = findings.get(relative);
    if (!g) return null;
    const p = g.problems.length;
    const n = g.information.length;
    return (
      <button
        type="button"
        onClick={() => setReport({ focus: relative })}
        title="Open this file in the analysis report"
        className="rounded text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tres-action"
      >
        {p > 0 && <span className="font-semibold text-tres-warning">{p} to resolve</span>}
        {p > 0 && n > 0 && <span className="text-muted-foreground">, </span>}
        {n > 0 && (
          <span className="text-muted-foreground">
            {n} {n === 1 ? 'note' : 'notes'}
          </span>
        )}
      </button>
    );
  }

  function sheetActions(s: FolderSheetPlan) {
    const key = refLabel(s.sheet);
    const on = isOn(key);
    return (
      <span className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={on}
          onChange={() => toggle(key)}
          aria-label={`Include ${key}`}
          title={on ? 'Included — uncheck to skip' : 'Skipped'}
        />
        <button
          type="button"
          disabled={!on || !canMap(s.sheet)}
          onClick={() => mapSheet(s.sheet)}
          className={BUTTON_CLASS}
        >
          Map
        </button>
      </span>
    );
  }

  function fileActions(f: FolderFilePlan) {
    const on = isOn(f.relative);
    const row = loadState[f.relative];
    const pending = loadable(f) && on && (!row || row.state === 'failed');
    const whole: FolderSheetRef = { file: f.relative, sheet: 'Sheet1' };
    return (
      <span className="flex flex-wrap items-center gap-2">
        {actionable(f) && (
          <input
            type="checkbox"
            checked={on}
            onChange={() => toggle(f.relative)}
            aria-label={`Include ${f.relative}`}
            title={on ? 'Included — uncheck to skip' : 'Skipped'}
          />
        )}
        {f.kind === 'sheet' && (
          <button
            type="button"
            disabled={!canMap(whole)}
            onClick={() => mapSheet(whole)}
            className={BUTTON_CLASS}
          >
            Map
          </button>
        )}
        {pending && (
          <>
            <select
              value={targetGraphOf(f) ?? ''}
              onChange={(e) =>
                setGraphChoice((prev) => ({ ...prev, [f.relative]: e.target.value }))
              }
              aria-label={`Target graph for ${f.relative}`}
              title="The named graph this file loads into — the proposal is a content guess; correct it here"
              className={SELECT_CLASS}
            >
              {targetGraphOf(f) === null && (
                <option value="" disabled>
                  Pick a graph
                </option>
              )}
              {GRAPH_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
            <select
              value={modeChoice[f.relative] ?? 'append'}
              onChange={(e) =>
                setModeChoice((prev) => ({
                  ...prev,
                  [f.relative]: e.target.value as 'append' | 'replace',
                }))
              }
              aria-label={`Load mode for ${f.relative}`}
              title="Append adds to the target graph; Replace clears it first"
              className={SELECT_CLASS}
            >
              <option value="append">Append</option>
              <option value="replace">Replace</option>
            </select>
            {f.proposed_graph != null && targetGraphOf(f) !== f.proposed_graph && (
              <span className="text-tres-warning" title="Differs from the proposed graph">
                moved
              </span>
            )}
          </>
        )}
      </span>
    );
  }

  function renderRow(row: FolderFileRow) {
    const cell = 'px-3 py-1 align-top';
    const only = row.sheets.length === 1 ? row.sheets[0] : undefined;
    if (only) {
      const dim = !isOn(refLabel(only.sheet)) && 'opacity-50';
      return (
        <tr className="border-t border-border">
          <td className={cell}>{sheetActions(only)}</td>
          <td className={cn(cell, 'break-words', dim)}>{row.relative}</td>
          <td className={cn(cell, dim)}>{describeRow(row)}</td>
          <td className={cell}>{findingsButton(row.relative)}</td>
          <td className={cell}>{statusText(sheetStatus(only))}</td>
        </tr>
      );
    }
    if (row.sheets.length > 1) {
      return (
        <>
          <tr className="border-t border-border">
            <td className={cell} />
            <td className={cn(cell, 'break-words font-medium')}>{row.relative}</td>
            <td className={cell}>{describeRow(row)}</td>
            <td className={cell}>{findingsButton(row.relative)}</td>
            <td className={cell}>{statusText(workbookStatus(row.sheets))}</td>
          </tr>
          {row.sheets.map((s) => {
            const dim = !isOn(refLabel(s.sheet)) && 'opacity-50';
            return (
              <tr key={s.sheet.sheet} className="border-t border-border/60">
                <td className={cell}>{sheetActions(s)}</td>
                <td className={cn(cell, 'break-words pl-8', dim)}>{s.sheet.sheet}</td>
                <td className={cn(cell, dim)}>{describeSheet(s)}</td>
                <td className={cell} />
                <td className={cell}>{statusText(sheetStatus(s))}</td>
              </tr>
            );
          })}
        </>
      );
    }
    if (row.file) {
      const f = row.file;
      const dim = (!actionable(f) || !isOn(f.relative)) && 'opacity-50';
      return (
        <>
          <tr className="border-t border-border">
            <td className={cell}>{fileActions(f)}</td>
            <td className={cn(cell, 'break-words', dim)}>{row.relative}</td>
            <td className={cn(cell, dim)}>{describeRow(row)}</td>
            <td className={cell}>{findingsButton(row.relative)}</td>
            <td className={cell}>{statusText(fileStatus(f))}</td>
          </tr>
          {f.bare_prefix && (
            <tr>
              <td colSpan={5} className="px-3 pb-2">
                <BarePrefixNotice
                  relative={f.relative}
                  claim={f.bare_prefix}
                  convert={convertChoice[f.relative] ?? false}
                  onConvert={(on) => setConvertChoice((prev) => ({ ...prev, [f.relative]: on }))}
                />
              </td>
            </tr>
          )}
        </>
      );
    }
    const on = isOn(row.relative);
    return (
      <tr className="border-t border-border">
        <td className={cell} />
        <td className={cn(cell, 'break-words', !on && 'opacity-50')}>{row.relative}</td>
        <td className={cn(cell, !on && 'opacity-50')}>{describeRow(row)}</td>
        <td className={cell}>{findingsButton(row.relative)}</td>
        <td className={cell}>
          {statusText(on ? { label: 'Cannot read', tone: 'error' } : { label: 'Skipped', tone: 'muted' })}
        </td>
      </tr>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Analyze a folder</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a folder and TReS proposes how to load each file in it.
        </p>
        <details className="mt-1 text-xs text-muted-foreground">
          <summary className="w-fit cursor-pointer rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tres-action">
            How folder analysis works
          </summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            <li>
              A picked folder gives its own files; subfolders are left alone. A zip of a dataset gives
              every file under its folder: ontology, vocabulary, catalog and data load into the graph
              of that name, any other folder into a graph named for it, and a fixtures folder is left
              out. A native file that carries rules loads them with it.
            </li>
            <li>
              Spreadsheets and tables (CSV, Excel, JSON, Markdown or HTML) are profiled together. Each
              gets a proposed mapping you can load as is or adjust in the Spreadsheet wizard.
            </li>
            <li>
              Graph files (Turtle, N-Triples) and native QROS files are checked for ontology, vocabulary
              or instance data, and load into the matching graph.
            </li>
            <li>
              Documents are indexed in the catalog: what each one is and which known things it mentions.
              Their text is not stored.
            </li>
            <li>Nothing loads until you choose to, and you can leave out any file.</li>
            <li>Anything that needs a decision is listed in the analysis report, with a way to resolve it.</li>
          </ul>
        </details>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        // Non-standard but universal: folder pick. Only top-level contents are
        // staged; anything in subfolders is filtered out client-side.
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) void analyze(files);
        }}
      />

      <input
        ref={zipRef}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void analyzeArchive(file);
        }}
      />

      {phase === 'pick' && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploadsLocked}
            className={cn(buttonVariants({ variant: 'secondary', size: 'md' }), 'border-dashed')}
          >
            Choose a folder to analyze
          </button>
          <button
            type="button"
            onClick={() => zipRef.current?.click()}
            disabled={uploadsLocked}
            className={cn(buttonVariants({ variant: 'secondary', size: 'md' }), 'border-dashed')}
          >
            Upload a .zip of a dataset
          </button>
          {uploadsLocked && (
            <p className="text-xs text-muted-foreground">
              Paused while an import is running — analyzing a folder stages files, so it waits too.
            </p>
          )}
        </div>
      )}

      {(phase === 'uploading' || phase === 'analyzing') && (
        <div className="rounded-md border border-border px-4 py-3 text-sm">
          {phase === 'uploading'
            ? `Staging files… ${progress.done} of ${progress.total}`
            : 'Analyzing the corpus…'}
        </div>
      )}

      {phase === 'error' && (
        <div className="space-y-2">
          <div className={messageClass('warning', 'normal')}>
            {error}
          </div>
          <button
            type="button"
            onClick={reset}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Start over
          </button>
        </div>
      )}

      {phase === 'plan' && plan && sessionId && mappingOpened && (
        <div className={cn(!mappingSheets && 'hidden')}>
          <FolderMappingPanel
            // A different set of sheets is a different mapping: it starts again.
            key={`${sessionId}|${plan.sheets
              .filter((s) => s.disposition === 'import' && isOn(refLabel(s.sheet)))
              .map((s) => refLabel(s.sheet))
              .join('|')}`}
            sessionId={sessionId}
            plans={plan.sheets.filter((s) => s.disposition === 'import' && isOn(refLabel(s.sheet)))}
            savedLinks={(plan.saved_links ?? []).flatMap((l) => {
              const link = linkFromSaved(l);
              return link ? [link] : [];
            })}
            token={idToken}
            onBack={() => setMappingSheets(false)}
          />
        </div>
      )}
      {phase === 'plan' && plan && !mappingSheets && (
        <div className="space-y-4">
          {/* The pages over every sheet: what each column is, the links between
              sheets, the properties, then one import (ruled 2026-09-30, 4.11).
              The plan carries each sheet's first rows and profile, so the
              pages read nothing again. */}
          {plan.sheets.some((s) => s.disposition === 'import') && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-border px-3 py-2 text-xs">
              <button
                type="button"
                disabled={!plan.sheets.some((s) => s.disposition === 'import' && isOn(refLabel(s.sheet)))}
                onClick={() => {
                  setMappingOpened(true);
                  setMappingSheets(true);
                }}
                className={buttonVariants({ variant: 'primary', size: 'md' })}
              >
                Map the sheets together ({plan.sheets.filter((s) => s.disposition === 'import' && isOn(refLabel(s.sheet))).length})
              </button>
              <span className="text-muted-foreground">
                One Columns page over every included sheet, the links between sheets, the properties, then one import.
              </span>
            </div>
          )}
          {/* Lead with the action, not a note: the load control takes the top,
              actions on the left (UAT 2026-09-02). */}
          {(() => {
            const sel = batchSelection();
            const total = sel.files.length + sel.sheets.length;
            const replacing = sel.files.filter((f) => f.mode === 'replace').length;
            return (
              <div className="rounded-md border border-border px-3 py-2 text-xs">
                <div className="flex flex-wrap items-center gap-3">
                  {total === 0 && !batchRunning ? (
                    <span className="text-muted-foreground">
                      {batchReport
                        ? 'Everything included has loaded.'
                        : 'Nothing selected to load.'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={total === 0 || batchRunning}
                      onClick={() =>
                        replacing > 0 && replaceConfirm?.scope !== 'all'
                          ? setReplaceConfirm({ scope: 'all' })
                          : void runBatch(sel)
                      }
                      className={buttonVariants({ variant: 'secondary', size: 'md' })}
                    >
                      {batchRunning
                        ? batchProgress
                          ? `Loading ${Math.min(batchProgress.done + 1, batchProgress.total)} of ${batchProgress.total}…`
                          : 'Loading everything…'
                        : `Load everything included (${total})`}
                    </button>
                  )}
                  {batchRunning && batchProgress?.current && (
                    <span className="font-mono text-muted-foreground">{batchProgress.current}</span>
                  )}
                  {(total > 0 || batchRunning) && (
                    <span className="text-muted-foreground">
                      {sel.files.length} file{sel.files.length === 1 ? '' : 's'}
                      {sel.sheets.length > 0 &&
                        `, ${sel.sheets.length} sheet${sel.sheets.length === 1 ? '' : 's'}`}{' '}
                      — each <span className="font-semibold">appends</span> to its proposed graph
                      {replacing > 0 && (
                        <span className="font-semibold text-tres-error">
                          {` (${replacing} set to Replace)`}
                        </span>
                      )}
                      ; schema files load first; one failure never stops the rest.
                      {total > 0 && !batchRunning && (
                        <>
                          {' '}
                          Estimated load time:{' '}
                          <span className="font-semibold">
                            {formatLoadEstimate(estimateLoadSeconds(sel))}
                          </span>
                          .
                        </>
                      )}
                    </span>
                  )}
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setReport({ focus: null })}
                      className={buttonVariants({ variant: 'secondary', size: 'md' })}
                    >
                      Analysis report
                      {totals.problems > 0 && (
                        <span className="font-semibold text-tres-warning">
                          {` (${totals.problems} to resolve)`}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={reset}
                      className={buttonVariants({ variant: 'secondary', size: 'md' })}
                    >
                      Analyze another folder
                    </button>
                  </div>
                </div>
                {replaceConfirm?.scope === 'all' && (
                  <div className={cn(messageClass('warning', 'compact'), 'mt-2 flex flex-wrap items-center gap-3')}>
                    <span>
                      {replacing} file{replacing === 1 ? '' : 's'} will REPLACE the contents of{' '}
                      {replacing === 1 ? 'its' : 'their'} target graph{replacing === 1 ? '' : 's'}.
                      Proceed?
                    </span>
                    <button
                      type="button"
                      onClick={() => void runBatch(sel)}
                      className={buttonVariants({ variant: 'danger', size: 'sm' })}
                    >
                      Load with replace
                    </button>
                    <button
                      type="button"
                      onClick={() => setReplaceConfirm(null)}
                      className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {batchReport && (
                  <div className="mt-2 space-y-1">
                    <p className="font-semibold">{batchReport.note}</p>
                    {(() => {
                      const restated = restatedCount(batchReport);
                      return restated ? (
                        <p className="text-muted-foreground">
                          {restated.read.toLocaleString()} statements read, {restated.added.toLocaleString()} new;{' '}
                          {restated.restated.toLocaleString()} restated a fact already held, which stays one statement
                          with its latest @updated and every @source.
                        </p>
                      ) : null;
                    })()}
                    {(() => {
                      const { updated, notAppliedOlder } = valueUpdates(batchReport);
                      const sentence = valueUpdatesSentence(updated, notAppliedOlder);
                      return sentence ? <p className="text-muted-foreground">{sentence}</p> : null;
                    })()}
                    {batchReport.graphs.length > 0 && (
                      <div className="text-muted-foreground">
                        {batchReport.graphs.map((g) => (
                          <p key={g.graph}>
                            {graphLabel(g.graph)}:{' '}
                            {g.before == null ? 'unknown' : g.before.toLocaleString()}
                            {' → '}
                            {g.after == null ? 'unknown' : g.after.toLocaleString()}
                            {' statements'}
                            {g.before != null && g.after != null && (
                              <span>
                                {' '}
                                ({g.after >= g.before ? '+' : ''}
                                {(g.after - g.before).toLocaleString()})
                              </span>
                            )}
                          </p>
                        ))}
                      </div>
                    )}
                    {batchReport.items
                      .filter((i) => !i.ok)
                      .map((i, n) => (
                        <p key={n} className="text-tres-error">
                          {i.item} — {i.detail}
                        </p>
                      ))}
                    {batchReport.items
                      .filter((i) => i.ok && i.item.includes(' :: '))
                      .map((i, n) => (
                        <p key={`s${n}`} className="text-muted-foreground">
                          {i.item} — {i.detail}
                        </p>
                      ))}
                  </div>
                )}
              </div>
            );
          })()}

          {(() => {
            const groups = new Map<string, FolderFilePlan[]>();
            for (const f of plan.files) {
              if (!loadable(f) || f.disposition !== 'load' || !f.proposed_graph) continue;
              const key = f.proposed_graph;
              groups.set(key, [...(groups.get(key) ?? []), f]);
            }
            const shared = [...groups.entries()].filter(([, files]) => files.length > 1);
            if (shared.length === 0) return null;
            return (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-border px-3 py-2 text-xs">
                <span className="font-medium">Set a group at once</span>
                {shared.map(([proposed, files]) => {
                  const first = files[0];
                  if (!first) return null;
                  const groupGraphs = new Set(files.map(targetGraphOf));
                  const groupModes = new Set(files.map((f) => modeChoice[f.relative] ?? 'append'));
                  const graph = groupGraphs.size === 1 ? [...groupGraphs][0]! : '__mixed__';
                  const mode = groupModes.size === 1 ? [...groupModes][0]! : '__mixed__';
                  return (
                    <span key={proposed} className="flex flex-wrap items-center gap-2">
                      <span>
                        {files.length} {first.content ?? first.kind} files
                      </span>
                      <span className="text-muted-foreground">into</span>
                      <select
                        value={graph}
                        onChange={(e) => {
                          if (e.target.value !== '__mixed__') setGroup(files, { graph: e.target.value });
                        }}
                        aria-label={`Target graph for the ${first.content ?? first.kind} files`}
                        className={SELECT_CLASS}
                      >
                        {graph === '__mixed__' && (
                          <option value="__mixed__" disabled>
                            mixed — see the table
                          </option>
                        )}
                        {GRAPH_OPTIONS.map((g) => (
                          <option key={g.value} value={g.value}>
                            {g.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={mode}
                        onChange={(e) => {
                          if (e.target.value !== '__mixed__') {
                            setGroup(files, { mode: e.target.value as 'append' | 'replace' });
                          }
                        }}
                        aria-label={`Load mode for the ${first.content ?? first.kind} files`}
                        title="Append adds to the target graph; Replace clears it first"
                        className={SELECT_CLASS}
                      >
                        {mode === '__mixed__' && (
                          <option value="__mixed__" disabled>
                            mixed
                          </option>
                        )}
                        <option value="append">Append</option>
                        <option value="replace">Replace</option>
                      </select>
                    </span>
                  );
                })}
              </div>
            );
          })()}

          <div className="rounded-md border border-border">
            <p className="px-3 py-2 text-xs font-semibold">Files ({rows.length})</p>
            <div className="max-h-[32rem] overflow-auto">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-muted/80 text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-1 font-medium">Actions</th>
                    <th scope="col" className="px-3 py-1 font-medium">File</th>
                    <th scope="col" className="px-3 py-1 font-medium">What it is</th>
                    <th scope="col" className="px-3 py-1 font-medium">Findings</th>
                    <th scope="col" className="px-3 py-1 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Fragment key={row.relative}>{renderRow(row)}</Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <FolderAnalysisReport
            open={report !== null}
            focusFile={report?.focus ?? null}
            sections={reportSections}
            unsupported={skippedLocal}
            subfolderCount={subfolderCount}
            mapNeedsRepick={plan.sheets.length > 0 && pickedRef.current.size === 0}
            actions={reportActions}
            onClose={() => setReport(null)}
          />
        </div>
      )}
    </div>
  );
}

/** A staged file declares the prefix `:` for a namespace other than QROS's,
 *  which `:` always names (ruling 4.19). Said under the file's row, before
 *  anything loads, with the conversion where the file allows one. */
function BarePrefixNotice({
  relative,
  claim,
  convert,
  onConvert,
}: {
  relative: string;
  claim: BarePrefixClaim;
  convert: boolean;
  onConvert: (on: boolean) => void;
}) {
  const head = `Declares the prefix : for <${claim.namespace}>, and in TReS : always means QROS's own namespace.`;
  const what = claim.other_prefix
    ? `Convert writes its : terms as ${claim.other_prefix}: terms, as the file also names that namespace.`
    : claim.vendor
      ? `It is one of the ${claim.vendor}, which TReS does not import; Convert leaves its statements out.`
      : 'It will not load: declare a prefix of your own for it in the file, write its terms with that prefix instead of :, and stage the file again.';
  return (
    <div className={cn(messageClass('warning', 'compact'), 'flex flex-wrap items-center gap-x-3 gap-y-1')}>
      <span>
        {head} {what}
      </span>
      {claim.convertible && (
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={convert}
            onChange={(e) => onConvert(e.target.checked)}
            aria-label={`Convert ${relative}`}
          />
          Convert
        </label>
      )}
    </div>
  );
}
